import { useMemo } from 'react'
import { Space, TreeSelect, Typography } from 'antd'
import type { TreeSelectProps } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  buildOrgTree,
  orgTypeName,
  typeOf,
  type OrgNode,
  type OrgNodeType,
  type OrgTreeNode,
} from '@/features/org/orgApi'

/**
 * antd'ning O'Z tugun tipi.
 *
 * ⚠️ `DefaultOptionType` (`antd/es/select`) ISHLAMAYDI: bu yerda
 * `title` — JSX, u tipda esa `title` satr deb qaralgan joylar bor.
 * `OrgNodeMoveModal` da aynan shu tuzoq izohlangan.
 */
type TreeNode = NonNullable<TreeSelectProps['treeData']>[number]

interface OrgNodeTreeSelectProps {
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  /**
   * ⚠️ IXTIYORIY va ataylab: `Form.Item` ichida ishlatilganda antd
   * `value`/`onChange` ni O'ZI biriktiradi. Ularni chaqiruv joyida
   * qo'lda ham berish (masalan `value={null}`) antd'ning ichki
   * tartibiga tayanadigan mo'rt kod bo'lardi — filtrda boshqariladigan
   * qiymat kerak bo'lsa ikkalasi BIRGA beriladi.
   */
  value?: string | null
  onChange?: (value: string | null) => void
  disabled?: boolean
  loading?: boolean
  placeholder?: string
  /** Filtr sifatida ishlatilganda "hammasi" varianti kerak bo'ladi */
  allowClear?: boolean
  style?: React.CSSProperties
}

/**
 * Tashkiliy tugun tanlagichi — FAQAT xodim biriktirilishi mumkin
 * bo'lganlari.
 *
 * <h2>⚠️ ASOSIY QOIDA — NOM EMAS, IMKONIYAT (QARORLAR.md §19.3)</h2>
 * Tanlanadigan tugun mezoni bitta: uning TURIDA `hasEmployees = true`.
 * Bu yerda `'BRANCH'` yoki `'DEPARTMENT'` kabi satr YO'Q va bo'lmaydi
 * ham. Administrator "Uchastka" degan yangi tur ochib unga xodim
 * imkoniyatini bersa, bu ro'yxat darhol uni ham ko'rsatadi — bitta
 * qator kod o'zgarmasdan.
 *
 * Qoidani baza ham majburlaydi (`hr.employment_guard`), ya'ni bu yerdagi
 * cheklov HIMOYA emas, QULAYLIK: operator noto'g'ri tugunni tanlab,
 * saqlashga bosib, sababi tushunarsiz 400 olishi kerak emas.
 *
 * <h2>⚠️ OTA-BOBOLAR KO'RSATILADI, LEKIN TANLANMAYDI</h2>
 * Bosh ofis ostidagi "Texnik bo'lim" ni ko'rsatish uchun bosh ofisning
 * o'zi ham daraxtda turishi kerak. U `selectable: false` bilan chiziladi
 * va sababi yonida yoziladi. Ularni butunlay olib tashlash bo'limlarni
 * ildizga ko'chirib, tuzilmani YOLG'ON ko'rsatardi.
 *
 * <h2>⚠️ CHUQURLIK CHEKLANMAGAN (§19.2)</h2>
 * Daraxt rekursiv quriladi, qavatlar sanalmaydi.
 *
 * <h2>⚠️ QIDIRUV — ALOHIDA XARITA ORQALI</h2>
 * `treeNodeFilterProp="title"` bu yerda ishlamaydi, chunki `title` —
 * JSX. Shuning uchun `id → "nom kod"` xaritasi tuziladi va
 * `filterTreeNode` o'shandan qidiradi (`OrgNodeMoveModal` bilan bir xil
 * yechim).
 */
export function OrgNodeTreeSelect({
  nodes,
  types,
  value,
  onChange,
  disabled,
  loading,
  placeholder,
  allowClear,
  style,
}: OrgNodeTreeSelectProps) {
  const { t } = useTranslation()

  /** `id → qidiruv matni` — JSX sarlavha qidiruvga yaramaydi */
  const searchText = useMemo(() => {
    const map = new Map<string, string>()
    for (const node of nodes) {
      map.set(node.id, `${node.displayName} ${node.code}`.toLowerCase())
    }
    return map
  }, [nodes])

  const treeData = useMemo<TreeNode[]>(() => {
    /**
     * Tugun tanlanadimi.
     *
     * ⚠️ Turi ro'yxatda TOPILMASA — TANLANMAYDI. `canPlaceUnder` dagi
     * teskari qaror ("tur noma'lum — taqiq yo'q") boshqa savolga
     * tegishli edi: u yerda gap TAKLIFNI cheklash haqida, bu yerda esa
     * yaroqsiz tanlovni taklif QILMASLIK haqida. Noma'lum turni
     * tanlashga ruxsat bersak, baza uni baribir rad etardi.
     */
    const selectableOf = (node: OrgNode): boolean => {
      if (node.state === 'CLOSED') return false
      return typeOf(types, node.kind)?.hasEmployees === true
    }

    const convert = (node: OrgTreeNode): TreeNode | null => {
      const children = node.children
        .map(convert)
        .filter((child): child is TreeNode => child !== null)
      const selectable = selectableOf(node)

      // Na o'zi tanlanadi, na ostida tanlanadigani bor — daraxtda
      // turishining ma'nosi yo'q.
      if (!selectable && children.length === 0) return null

      const nodeType = typeOf(types, node.kind)
      return {
        value: node.id,
        selectable,
        // ⚠️ `disabled` EMAS: `disabled` butun shoxni bosib bo'lmaydigan
        // qilardi va ostidagi bo'limlar ham tanlanmasdi.
        title: (
          <Space size={6} wrap>
            <Typography.Text type={selectable ? undefined : 'secondary'}>
              {node.displayName}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {nodeType ? orgTypeName(nodeType, t) : null}
            </Typography.Text>
            {selectable ? null : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {node.state === 'CLOSED'
                  ? t('hr.node.closedNote')
                  : t('hr.node.noEmployeesNote')}
              </Typography.Text>
            )}
          </Space>
        ),
        children,
      }
    }

    return buildOrgTree(nodes)
      .map(convert)
      .filter((node): node is TreeNode => node !== null)
  }, [nodes, types, t])

  return (
    <TreeSelect
      // ⚠️ `undefined`, `null` EMAS: antd `null` ni "qiymat bor, u bo'sh"
      // deb emas, boshqarilmaydigan holat deb qabul qilishi mumkin.
      value={value ?? undefined}
      onChange={(next: string | undefined) => onChange?.(next ?? null)}
      treeData={treeData}
      showSearch
      treeDefaultExpandAll
      filterTreeNode={(input, node) => {
        const key = node?.value === undefined ? '' : String(node.value)
        return (searchText.get(key) ?? '').includes(input.toLowerCase())
      }}
      placeholder={placeholder ?? t('hr.node.placeholder')}
      disabled={disabled ?? false}
      loading={loading ?? false}
      allowClear={allowClear ?? false}
      notFoundContent={t('hr.node.empty')}
      style={{ width: '100%', ...style }}
    />
  )
}
